import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, MapPin, Globe, Map, Users, IndianRupee, TrendingUp,
  Percent, ChevronDown, ChevronUp, Building2, Phone, Mail,
  Download, Loader2, ExternalLink, Calendar, Filter,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (s) => {
  if (!s) return "-";
  const d = new Date(s);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};
const paxStr = (a, c) => {
  const adults = Number(a || 0); const children = Number(c || 0);
  if (!adults && !children) return "-";
  return children > 0 ? `${adults}A + ${children}C` : `${adults}A`;
};

const PERIOD_OPTIONS = [
  { value: "all", label: "All Time" },
  { value: "this_fy", label: "This Financial Year" },
  { value: "last_fy", label: "Last Financial Year" },
  { value: "current_month", label: "Current Month" },
];

const KpiTile = ({ label, value, accent, sub, icon: Icon }) => (
  <Card className="bg-white border-gray-200 shadow-sm">
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{label}</p>
        {Icon && <Icon className={`w-4 h-4 ${accent || "text-gray-400"}`} />}
      </div>
      <p className={`text-xl font-bold tabular-nums ${accent || "text-gray-900"}`}>{value}</p>
      {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

const payStatusBadge = (s) => {
  const map = {
    full: "bg-emerald-100 text-emerald-700",
    partial: "bg-amber-100 text-amber-700",
    pending: "bg-rose-100 text-rose-700",
  };
  return map[(s || "pending").toLowerCase()] || "bg-gray-100 text-gray-700";
};

const DestinationDetail = () => {
  const { name } = useParams();
  const destination = decodeURIComponent(name || "");
  const { token } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");
  const [expandedAgent, setExpandedAgent] = useState(null);

  const fetchData = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/destination-agents?destination=${encodeURIComponent(destination)}&period=${p}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load destination details");
    } finally {
      setLoading(false);
    }
  }, [destination, token]);

  useEffect(() => {
    if (destination && token) fetchData(period);
  }, [destination, token, period, fetchData]);

  // Derive KPIs from agent data
  const kpis = (() => {
    if (!data?.agents?.length) return null;
    let total_bookings = 0, total_booking_amount = 0, total_paid = 0, total_pax_adults = 0, total_pax_children = 0, total_gst = 0, total_profit = 0;
    for (const ag of data.agents) {
      total_bookings += ag.bookings_count || 0;
      total_booking_amount += ag.booking_amount || 0;
      total_paid += ag.paid_amount || 0;
      total_pax_adults += ag.pax_adults || 0;
      total_pax_children += ag.pax_children || 0;
      total_gst += ag.gst_amount || 0;
      total_profit += ag.net_profit || 0;
    }
    const total_balance = Math.max(0, total_booking_amount - total_paid);
    return { total_bookings, total_booking_amount, total_paid, total_balance, total_pax_adults, total_pax_children, total_gst, total_profit };
  })();

  const downloadExcel = () => {
    if (!data?.agents?.length) return;
    const rows = [["Booking #", "Agent", "Company", "Guest Name", "Travel Date", "Return Date", "Nights", "Pax", "Booking Amt", "Paid", "Balance", "Status"]];
    for (const ag of data.agents) {
      for (const b of ag.bookings || []) {
        rows.push([
          b.booking_number, ag.agent_name, ag.company_name, b.guest_name,
          fmtDate(b.travel_date), fmtDate(b.return_date), b.nights || "-",
          paxStr(b.pax_adults, b.pax_children),
          b.booking_amount, b.paid_amount, b.balance_amount, b.status || "booked",
        ]);
      }
    }
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${destination}_Destination_Report.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Excel file downloaded");
  };

  const isIntl = ["bali", "singapore", "dubai", "sri lanka", "malaysia", "vietnam", "bangkok", "thailand", "maldives", "europe", "usa", "australia"].some(
    (k) => destination.toLowerCase().includes(k)
  );

  return (
    <DashboardLayout type="admin">
      <div className="space-y-6" data-testid="destination-detail-page">

        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin")}
              className="w-9 h-9 rounded-xl border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
              data-testid="dest-back-btn"
            >
              <ArrowLeft className="w-4 h-4 text-gray-600" />
            </button>
            <div className="flex items-center gap-2">
              {isIntl
                ? <Globe className="w-5 h-5 text-cyan-600" />
                : <Map className="w-5 h-5 text-amber-600" />
              }
              <div>
                <h1 className="text-2xl font-bold text-gray-900" data-testid="dest-name-heading">
                  {destination}
                </h1>
                <p className="text-sm text-gray-500">Destination Wise Report</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Period Filter */}
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="text-sm text-gray-700 bg-transparent border-none outline-none cursor-pointer"
                data-testid="dest-period-filter"
              >
                {PERIOD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Download Excel */}
            <Button
              variant="outline"
              size="sm"
              onClick={downloadExcel}
              disabled={!data?.agents?.length}
              className="gap-2 rounded-xl"
              data-testid="dest-download-excel-btn"
            >
              <Download className="w-4 h-4" />
              Download Excel
            </Button>
          </div>
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            <span className="ml-3 text-gray-500">Loading destination data...</span>
          </div>
        )}

        {/* ── KPI Tiles ── */}
        {!loading && kpis && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="dest-kpi-tiles">
            <KpiTile label="Total Bookings" value={kpis.total_bookings} accent="text-gray-900" icon={Calendar} />
            <KpiTile label="Total Agents" value={data.agents.length} accent="text-indigo-600" icon={Users} />
            <KpiTile label="Total Pax" value={paxStr(kpis.total_pax_adults, kpis.total_pax_children)} accent="text-gray-700" icon={Users} />
            <KpiTile label="Booking Amt" value={inr(kpis.total_booking_amount)} accent="text-gray-900" icon={IndianRupee} />
            <KpiTile label="Paid Amt" value={inr(kpis.total_paid)} accent="text-emerald-600" icon={TrendingUp} />
            <KpiTile label="Balance Amt" value={inr(kpis.total_balance)} accent={kpis.total_balance > 0 ? "text-rose-600" : "text-emerald-600"} icon={IndianRupee} />
          </div>
        )}

        {/* ── Empty State ── */}
        {!loading && (!data?.agents?.length) && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <MapPin className="w-12 h-12 text-gray-300 mb-4" />
            <p className="text-gray-500 font-medium">No bookings found for {destination}</p>
            <p className="text-sm text-gray-400 mt-1">Try changing the time period filter.</p>
          </div>
        )}

        {/* ── Agent Accordion ── */}
        {!loading && data?.agents?.length > 0 && (
          <div className="space-y-3" data-testid="dest-agents-list">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-800">
                Agent Breakdown — <span className="text-emerald-600">{data.agents.length} agent{data.agents.length !== 1 ? "s" : ""}</span> with bookings
              </h2>
            </div>

            {data.agents.map((agent) => (
              <div key={agent.agent_id} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid={`dest-agent-card-${agent.agent_id}`}>
                {/* Agent Summary Row */}
                <div
                  className="flex items-center justify-between px-5 py-4 bg-gray-50 hover:bg-emerald-50/40 cursor-pointer transition-colors"
                  onClick={() => setExpandedAgent(expandedAgent === agent.agent_id ? null : agent.agent_id)}
                  data-testid={`dest-agent-row-${agent.agent_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm shrink-0">
                      {(agent.agent_name || "A").charAt(0).toUpperCase()}
                    </div>
                    <div className="w-44 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate">{agent.agent_name}</p>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        <span className="flex items-center gap-1 text-xs text-gray-500 truncate"><Building2 className="w-3 h-3 shrink-0" />{agent.company_name}</span>
                        {agent.phone && agent.phone !== "-" && (
                          <span className="flex items-center gap-1 text-xs text-gray-500"><Phone className="w-3 h-3 shrink-0" />{agent.phone}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 sm:gap-6">
                    <div className="text-center hidden sm:block">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wide">Bookings</p>
                      <p className="font-bold text-gray-800 text-sm">{agent.bookings_count}</p>
                    </div>
                    <div className="text-center hidden md:block">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wide">No. of Pax</p>
                      <p className="font-bold text-gray-800 text-sm">{paxStr(agent.pax_adults, agent.pax_children)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wide">Booking Amt</p>
                      <p className="font-bold text-gray-900 text-sm">{inr(agent.booking_amount)}</p>
                    </div>
                    <div className="text-center hidden sm:block">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wide">Paid Amt</p>
                      <p className="font-bold text-emerald-600 text-sm">{inr(agent.paid_amount)}</p>
                    </div>
                    <div className="text-center hidden sm:block">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wide">Balance Amt</p>
                      <p className={`font-bold text-sm ${agent.balance_amount > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                        {inr(agent.balance_amount)}
                      </p>
                    </div>
                    <div className="text-center hidden lg:block">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wide">GST Amt</p>
                      <p className="font-bold text-amber-600 text-sm">{inr(agent.gst_amount)}</p>
                    </div>
                    <div className="text-center hidden lg:block">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wide">Profit Amt</p>
                      <p className="font-bold text-indigo-600 text-sm">{inr(agent.net_profit)}</p>
                    </div>
                  <div className="flex items-center gap-2 ml-2">
                      <Link
                        to={`/admin/agents/${agent.agent_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline hidden lg:flex items-center gap-1"
                        data-testid={`dest-view-agent-${agent.agent_id}`}
                      >
                        Agent Detail <ExternalLink className="w-3 h-3" />
                      </Link>
                      <div className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg transition-colors ${
                        expandedAgent === agent.agent_id
                          ? "bg-emerald-600 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700"
                      }`}>
                        {expandedAgent === agent.agent_id
                          ? <><ChevronUp className="w-3.5 h-3.5" /> Collapse</>
                          : <><ChevronDown className="w-3.5 h-3.5" /> {agent.bookings_count} Booking{agent.bookings_count !== 1 ? "s" : ""}</>
                        }
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Bookings Table */}
                {expandedAgent === agent.agent_id && (
                  <div className="px-5 pb-4 pt-3 bg-white" data-testid={`dest-bookings-table-${agent.agent_id}`}>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                            <th className="py-2 pr-3 text-left font-semibold">Booking ID</th>
                            <th className="py-2 pr-3 text-left font-semibold">Guest Name</th>
                            <th className="py-2 pr-3 text-center font-semibold">No. of Pax</th>
                            <th className="py-2 pr-3 text-left font-semibold">Travel Date</th>
                            <th className="py-2 pr-3 text-center font-semibold">No. of Nights</th>
                            <th className="py-2 pr-3 text-right font-semibold">Booking Amt</th>
                            <th className="py-2 pr-3 text-right font-semibold">Paid Amt</th>
                            <th className="py-2 pr-3 text-right font-semibold">Balance Amt</th>
                            <th className="py-2 pr-3 text-right font-semibold">GST Amt</th>
                            <th className="py-2 pr-3 text-right font-semibold">Profit Amt</th>
                            <th className="py-2 pr-3 text-left font-semibold">Status</th>
                            <th className="py-2 text-center font-semibold">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {(agent.bookings || []).map((b) => (
                            <tr key={b.booking_id} className="hover:bg-gray-50 transition-colors">
                              <td className="py-2.5 pr-3">
                                <span className="font-mono text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">
                                  #{b.booking_number}
                                </span>
                              </td>
                              <td className="py-2.5 pr-3 text-gray-800 font-medium whitespace-nowrap">{b.guest_name}</td>
                              <td className="py-2.5 pr-3 text-center text-gray-700">
                                {paxStr(b.pax_adults, b.pax_children)}
                              </td>
                              <td className="py-2.5 pr-3 text-gray-600 whitespace-nowrap">
                                <span>{fmtDate(b.travel_date)}</span>
                                {b.return_date && (
                                  <span className="text-gray-400 text-xs block">→ {fmtDate(b.return_date)}</span>
                                )}
                              </td>
                              <td className="py-2.5 pr-3 text-center text-gray-600">
                                {b.nights ? `${b.nights}N` : "-"}
                              </td>
                              <td className="py-2.5 pr-3 text-right text-gray-800 font-semibold tabular-nums">
                                {inr(b.booking_amount)}
                              </td>
                              <td className="py-2.5 pr-3 text-right text-emerald-600 font-semibold tabular-nums">
                                {inr(b.paid_amount)}
                              </td>
                              <td className="py-2.5 pr-3 text-right font-semibold tabular-nums">
                                <span className={b.balance_amount > 0 ? "text-rose-600" : "text-emerald-600"}>
                                  {inr(b.balance_amount)}
                                </span>
                              </td>
                              <td className="py-2.5 pr-3 text-right text-amber-600 font-semibold tabular-nums">
                                {inr(b.gst_amount)}
                              </td>
                              <td className="py-2.5 pr-3 text-right text-indigo-600 font-semibold tabular-nums">
                                {inr(b.net_profit)}
                              </td>
                              <td className="py-2.5 pr-3">
                                <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium capitalize ${payStatusBadge(b.status)}`}>
                                  {b.status || "booked"}
                                </span>
                              </td>
                              <td className="py-2.5 text-center">
                                <Link
                                  to={`/admin/bookings/${b.booking_id}`}
                                  className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-800 hover:underline"
                                  data-testid={`dest-view-booking-${b.booking_id}`}
                                >
                                  View <ExternalLink className="w-3 h-3" />
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DestinationDetail;
